import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BlockRenderer } from '@/components/knowledge/BlockRenderer';
import { KnowledgeTree } from '@/components/knowledge/TreeNode';
import { SopCommentsPanel } from '@/components/knowledge/SopCommentsPanel';
import { DriveLinkCard } from '@/components/knowledge/DriveLinkCard';
import { FolderTree, Bookmark, Star, StarOff, AlertTriangle } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePrefs } from '@/lib/prefs';

export default function KnowledgePage() {
  const { pageId } = useParams();
  const nav = useNavigate();
  const [tree, setTree] = useState([]);
  const [page, setPage] = useState(null);
  const [suggestion, setSuggestion] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const { prefs, togglePin } = usePrefs();
  const isPinned = pageId && (prefs.pinned_page_ids || []).includes(pageId);

  useEffect(() => { api.get('/knowledge/tree').then(r => setTree(r.data || [])).catch(e => toast.error(parseErr(e))); }, []);
  useEffect(() => {
    if (!pageId) { setPage(null); return; }
    api.get(`/knowledge/pages/${pageId}`).then(r => setPage(r.data)).catch(e => { toast.error(parseErr(e)); nav('/knowledge'); });
  }, [pageId, nav]);

  const roots = useMemo(() => tree.filter(p => !p.parent_id), [tree]);
  const kids = useMemo(() => {
    const m = {}; tree.forEach(p => { if (p.parent_id) (m[p.parent_id] = m[p.parent_id] || []).push(p); });
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return m;
  }, [tree]);

  const sendSuggestion = async () => {
    if (!suggestion.trim() || !page) return;
    try { await api.post(`/knowledge/pages/${page.id}/suggest`, { suggestion }); toast.success('Sent to the page owner'); setSuggestion(''); setSuggestOpen(false); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Knowledge Hub', to: '/knowledge' }, ...(page ? [{ label: page.title }] : [])]}>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] min-h-[calc(100vh-56px)]" data-testid="knowledge-page">
        <aside className="border-r bg-[hsl(var(--surface-1))] p-3 max-h-[calc(100vh-56px)] overflow-y-auto" style={{ borderColor: 'hsl(var(--border))' }} data-testid="knowledge-sidebar-tree">
          <DriveLinkCard />
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] px-1 py-1 font-semibold">Library</div>
          <KnowledgeTree roots={roots} kids={kids} currentId={pageId} />
        </aside>
        <div className="min-w-0">
          {!page ? (
            <div className="p-8 text-center">
              <div className="font-heading text-2xl">Knowledge Hub</div>
              <p className="mt-2 text-[hsl(var(--foreground-muted))]">Choose a page from the tree — SOP A–H, templates, email library, or the pinned Quick Reference.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 max-w-[720px] mx-auto">
                {roots.slice(0, 6).map(r => (
                  <Link key={r.id} to={`/knowledge/${r.id}`} className="border rounded-xl bg-[hsl(var(--card))] p-4 text-left hover:border-[hsl(var(--border-strong))]" style={{ borderColor: 'hsl(var(--border))' }}>
                    <div className="flex items-center gap-2"><FolderTree size={16} /> <span className="font-heading font-semibold">{r.title}</span></div>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="fade-up">
              <div className="max-w-[900px] mx-auto px-8 pt-6">
                <div className="page-hero-cover">
                  <div className="page-hero-emoji">{page.icon || page.metadata?.emoji || '📄'}</div>
                </div>
              </div>
              <div className="page-canvas pt-8">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">
                    {page.kind === 'sop' ? 'Standard Operating Procedure' : page.kind === 'email_template' ? 'Email template' : page.kind === 'template' ? 'Template' : page.kind === 'quick_ref' ? 'Quick reference' : page.kind}
                  </div>
                  <Button
                    size="sm"
                    variant={isPinned ? 'default' : 'outline'}
                    onClick={() => togglePin(page.id, !isPinned)}
                    data-testid="knowledge-pin-toggle"
                  >
                    {isPinned ? <><Star size={13} className="mr-1 fill-current" />Pinned</> : <><StarOff size={13} className="mr-1" />Pin to sidebar</>}
                  </Button>
                </div>
                <h1 data-testid="knowledge-page-title">{page.title}</h1>
                {page.metadata?.needs_confirmation && (
                  <div className="chip chip-warning inline-flex items-center gap-1 mt-1"
                       data-testid="knowledge-needs-confirmation-badge"
                       title="This SOP has one or more [CONFIRM — ...] markers awaiting owner sign-off.">
                    <AlertTriangle size={11} />Needs confirmation
                  </div>
                )}
                {page.metadata?.sop_code && (
                  <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-1 font-mono">
                    {page.metadata.sop_code} · Category {page.metadata.sop_category}
                    {page.metadata.sop_owner ? ` · Owner: ${page.metadata.sop_owner}` : ''}
                  </div>
                )}
                {page.metadata?.pinned && <div className="chip chip-accent inline-flex mt-1"><Bookmark size={12} className="mr-1" />Pinned system-wide</div>}
                <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  {page.metadata?.owner_role && <span>Owner: <b className="text-[hsl(var(--foreground))]">{page.metadata.owner_role.replace('_', ' ')}</b></span>}
                  {page.metadata?.contributors && <span>· Contributors: {page.metadata.contributors}</span>}
                  {page.metadata?.version && <span>· Version {page.metadata.version}</span>}
                  {page.metadata?.last_reviewed && <span>· Last reviewed {fmtDate(page.metadata.last_reviewed)}</span>}
                  <span>· Updated {fmtDate(page.updated_at)}</span>
                </div>
                <div className="mt-6">
                  <BlockRenderer blocks={page.blocks} />
                </div>
                <SopCommentsPanel page={page} />
                <div className="mt-10 border-t pt-4" style={{ borderColor: 'hsl(var(--border))' }}>
                  <Button variant="outline" size="sm" data-testid="knowledge-suggest-open" onClick={() => setSuggestOpen(v => !v)}>
                    Suggest a change
                  </Button>
                  {suggestOpen && (
                    <div className="mt-2 space-y-2">
                      <Textarea data-testid="knowledge-suggest-textarea" value={suggestion} onChange={e => setSuggestion(e.target.value)} placeholder="What should change? Be specific." rows={3} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={sendSuggestion} data-testid="knowledge-suggest-send">Send to owner</Button>
                        <Button size="sm" variant="ghost" onClick={() => setSuggestOpen(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
